/**
 * ArtifactViewer
 * Renders an artifact's preview in-browser from its canonical doc_model — so we
 * never round-trip the binary just to show it. Picks a renderer per format:
 *   markdown / docx -> ReactMarkdown over the markdown source
 *   pptx            -> styled HTML slide cards from the slides model
 *   xlsx            -> a real grid: one table per sheet, header row frozen-styled
 *   html            -> sandboxed iframe of the HTML document
 *   gforms          -> question list preview + (once created) the form link
 */

import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Empty, Spin } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import api from '@/services/api';
import type { Artifact, FormulaValues } from '@/types/models';
// ONE deck renderer for the whole product - the Library viewer and the
// meta-agent's Library-file preview render through this same component, so a
// deck cannot look like a different object depending on where it is opened.
import { SlideDeckView, type Slide } from '@components/common/SlideDeckView';
import PdfPagesView from './PdfPagesView';
import BundleView from './BundleView';
import { shownCell, type ShownCell } from './sheetCells';

interface Props {
  artifact: Artifact;
}

// Numbers, errors and formulas read left to right in a Hebrew grid too; the
// <bdi> isolates the run without re-aligning the cell.
const CellText: React.FC<{ shown: ShownCell }> = ({ shown }) =>
  shown.ltr ? <bdi dir="ltr">{shown.text}</bdi> : <>{shown.text}</>;

// The dressing a cell carries (bold, fill, colour, align) is document content,
// not app chrome, which is why it goes inline. What the cell SHOWS - a
// formula's computed value - is sheetCells.ts.
const cellStyle = (cell: any): React.CSSProperties | undefined => {
  if (cell === null || typeof cell !== 'object') return undefined;
  const style: React.CSSProperties = {};
  if (cell.bold) style.fontWeight = 700;
  if (cell.italic) style.fontStyle = 'italic';
  if (cell.fill) style.backgroundColor = `#${String(cell.fill).replace('#', '')}`;
  if (cell.color) style.color = `#${String(cell.color).replace('#', '')}`;
  if (cell.align) style.textAlign = cell.align;
  return Object.keys(style).length ? style : undefined;
};

const SheetsRenderer: React.FC<{
  sheets: any[];
  values?: FormulaValues | null;
  sheetName: (n: number) => string;
}> = ({ sheets, values, sheetName }) => (
  <div className="artifact-sheets">
    {sheets.map((sheet, si) => {
      const rows: any[][] = Array.isArray(sheet?.rows) ? sheet.rows : [];
      // `header: false` marks a non-table sheet (a title block, notes) —
      // every row is body and nothing gets the header band.
      const hasHeader = sheet?.header !== false;
      const header = hasHeader ? rows[0] : undefined;
      const body = hasHeader ? rows.slice(1) : rows;
      const firstBodyRow = hasHeader ? 2 : 1;
      return (
        <section className="artifact-sheet" key={sheet?.name ?? si}>
          <div className="artifact-sheet__tab">{sheet?.name || sheetName(si + 1)}</div>
          <div className="artifact-sheet__scroll">
            <table className="artifact-sheet__table">
              {header && (
                <thead>
                  <tr>
                    <th className="artifact-sheet__gutter" aria-hidden="true" />
                    {header.map((cell: any, ci: number) => {
                      const shown = shownCell(cell, values, si, 0, ci);
                      return <th key={ci} title={shown.title}><CellText shown={shown} /></th>;
                    })}
                  </tr>
                </thead>
              )}
              <tbody>
                {body.map((row, ri) => (
                  <tr key={ri}>
                    <td className="artifact-sheet__gutter">{ri + firstBodyRow}</td>
                    {(Array.isArray(row) ? row : []).map((cell: any, ci: number) => {
                      const shown = shownCell(cell, values, si, ri + firstBodyRow - 1, ci);
                      return (
                        <td key={ci} className={shown.className} title={shown.title} style={cellStyle(cell)}>
                          <CellText shown={shown} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    })}
  </div>
);


const FormPreview: React.FC<{ artifact: Artifact }> = ({ artifact }) => {
  const { t } = useLanguage();
  const model = artifact.doc_model || {};
  const responder: string = model.responder_uri || artifact.form_url || '';
  if (!responder) {
    return <Empty description={t('chat.artifacts.generating')} />;
  }
  const embedSrc = responder + (responder.includes('?') ? '&' : '?') + 'embedded=true';
  return (
    <div className="artifact-form">
      {/* Small elegant link that floats over the embed's top corner, so it
          never steals vertical space from the live preview. */}
      <a
        className="artifact-form__open-btn"
        href={responder}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ExportOutlined />
        {t('chat.artifacts.openForm')}
      </a>
      {/* Live preview of the real Google Form, embedded. */}
      <iframe
        className="artifact-form__frame"
        title={model.title || 'Google Form'}
        src={embedSrc}
      />
    </div>
  );
};

const ArtifactViewer: React.FC<Props> = ({ artifact }) => {
  const { t } = useLanguage();
  // Render the version the user is viewing (if any), else the current model.
  const model = artifact.viewing?.doc_model ?? artifact.doc_model;
  // Apple outputs preview as their Office twins: same model, same schematic.
  // A PDF artifact previews as its Word twin for the schematic, and the
  // exact page view comes from preview.pdf, which serves its own bytes.
  const fmt = ({ numbers: 'xlsx', pdf: 'docx' } as Record<string, typeof artifact.output_format>)[artifact.output_format || ''] ?? artifact.output_format;

  // Branded preview: for pptx/docx, ask the backend to render the tenant's
  // design-skill HTML (real fonts/colors/logo/layouts) so the panel matches the
  // downloaded file. Tri-state so we never flash the unbranded schematic first:
  //   undefined = loading (show spinner)   null = no design skill (show schematic)
  //   string    = branded HTML to render
  // The stamp decides, not the live toggle: an artifact created while the
  // branding switch was OFF shows its plain schematic forever, even beside a
  // branded sibling in the same conversation. Absent (legacy) means branded.
  const wantsBrand = (fmt === 'pptx' || fmt === 'docx') && artifact.branded !== false;
  // undefined = loading, null = no branded preview (show schematic), string = HTML.
  const [brandedHtml, setBrandedHtml] = useState<string | null | undefined>(
    wantsBrand ? undefined : null,
  );
  // Re-fetch whenever the MODEL itself changes — not just id/version. On creation
  // the version is set before doc_model finishes loading, so keying on version
  // alone meant the fetch ran once with an empty model and never re-ran when the
  // model arrived (the "only branded after refresh" bug). A content signature of
  // the model fixes that: it changes from "none" → real content once it loads.
  const modelSig = model
    ? `${artifact.viewing?.version ?? artifact.current_version ?? 0}:${JSON.stringify(model).length}`
    : 'none';
  const previewKey = `${artifact.artifact_id}:${fmt}:${modelSig}`;

  // The EXACT preview: the real .docx laid out by a document engine on the
  // server, shown as pages. undefined = fetching, null = not available
  // (no engine, not rendered yet, or a version being viewed from history),
  // string = object URL of the PDF. Keyed by the version so an edit never
  // shows the previous version's pages.
  const exactKey = `${artifact.artifact_id}:${artifact.viewing?.version ?? artifact.current_version ?? 0}`;
  // Every version goes through the engine — a past version shown as the
  // HTML look-alike next to the current one's real pages read as "the old
  // preview came back".
  const wantsExact = fmt === 'docx' && artifact.status === 'ready';
  const viewedVersion = artifact.viewing?.version;
  const [exactPdf, setExactPdf] = useState<ArrayBuffer | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    if (!wantsExact) {
      setExactPdf(null);
      return;
    }
    setExactPdf(undefined);
    // The pages are laid out by the server right after the render; the first
    // request can arrive while that is still running (409 "not rendered
    // yet") or while the engine is busy (503). Keep asking for a while before
    // giving up — the alternative was the old look-alike for ten seconds and
    // then a jump to the real pages.
    let timer = 0;
    let attempts = 0;
    const ask = () => {
      attempts += 1;
      api.get(`/files/artifacts/${artifact.artifact_id}/preview.pdf`, {
        responseType: 'arraybuffer',
        params: viewedVersion !== undefined ? { version: viewedVersion } : undefined,
      })
        .then((res) => { if (!cancelled) setExactPdf(res.data as ArrayBuffer); })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          // 410 (a pruned version) falls through to the look-alike below.
          if ((status === 409 || status === 503 || status === 502 || !status) && attempts < 20) {
            timer = window.setTimeout(ask, 1500);
            return;
          }
          setExactPdf(null);
        });
    };
    ask();
    return () => { cancelled = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exactKey, wantsExact]);
  useEffect(() => {
    let cancelled = false;
    if (!model || !wantsBrand) {
      setBrandedHtml(null);
      return;
    }
    setBrandedHtml(undefined); // loading — hold the schematic back until we know
    api.post('/conversations/artifact-brand-preview', {
      format: fmt, doc_model: model,
      // The creation-time stamp: 'default' makes the server mirror the
      // platform default brand instead of the tenant's.
      brand_source: artifact.brand_source ?? null,
      draft_conversation_id: artifact.draft_conversation_id ?? null,
    })
      .then(res => { if (!cancelled) setBrandedHtml((res.data?.html as string) || null); })
      .catch(() => { if (!cancelled) setBrandedHtml(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  if (artifact.status === 'error') {
    return <Empty description={artifact.error || t('chat.artifacts.error')} />;
  }
  if (!model) {
    return <Empty description={t('chat.artifacts.generating')} />;
  }

  // The exact preview: real pages of the real Word file. The browser's own
  // PDF renderer draws them — page breaks, running header/footer, embedded
  // fonts and colors are those of the document itself, not a look-alike.
  if (fmt === 'docx' && exactPdf) {
    return <PdfPagesView data={exactPdf} title={artifact.title} />;
  }

  // While the exact pages are being laid out, show pages-to-be — never the
  // look-alike, which read as "the old version" for the first seconds and
  // then jumped to the real thing.
  if (fmt === 'docx' && exactPdf === undefined) {
    return (
      <div className="artifact-pdf artifact-pdf--pending" role="status" aria-live="polite">
        <div className="artifact-pdf__pages">
          <div className="artifact-pdf-page-wrap artifact-pdf-skeleton" />
          <div className="artifact-pdf-page-wrap artifact-pdf-skeleton artifact-pdf-skeleton--second" />
        </div>
        <div className="artifact-pdf__count">{t('chat.artifacts.preparingExact')}</div>
      </div>
    );
  }

  // Branded HTML preview (design skill present) — render the real design.
  // For docx this is the fallback when no layout engine is available.
  if (wantsBrand && typeof brandedHtml === 'string') {
    return (
      <div className="artifact-approx">
        <iframe
          className="artifact-html-frame"
          title={artifact.title || 'preview'}
          sandbox="allow-same-origin"
          srcDoc={brandedHtml}
        />
      </div>
    );
  }
  // Still resolving the branded preview — show a loader, NOT the schematic, so
  // the first thing the user sees is the design (no two-stage flash).
  if (wantsBrand && brandedHtml === undefined) {
    return (
      <div className="artifact-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 240 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (fmt === 'pptx') {
    return <SlideDeckView slides={(model.slides || []) as Slide[]} />;
  }
  if (fmt === 'xlsx') {
    // The doc_model carries the workbook's own dressing — header band, fills,
    // bold, number alignment — so it renders as a real, coloured Excel grid
    // rather than a bare table. It IS what the saved bytes were built from, so
    // the preview is faithful; the Library's file preview uses the identical
    // look (see FilePreviewContent) for files opened straight from storage.
    return (
      <SheetsRenderer
        sheets={model.sheets || []}
        values={artifact.viewing ? artifact.viewing.formula_values : artifact.formula_values}
        sheetName={(n) => t('chat.artifacts.sheetFallbackName', { n })}
      />
    );
  }
  if (fmt === 'html') {
    return (
      <iframe
        className="artifact-html-frame"
        title={artifact.title || 'preview'}
        sandbox="allow-same-origin"
        srcDoc={model.html || ''}
      />
    );
  }
  if (fmt === 'zip') {
    // A packaged bundle: nothing to page through, everything to download.
    return <BundleView artifact={artifact} />;
  }
  if (fmt === 'gforms') {
    // Full-bleed embed of the live Google Form (not on a narrow doc page).
    return <FormPreview artifact={artifact} />;
  }
  // markdown + docx both preview from a markdown `source`, rendered on a
  // realistic document "page" (white sheet, margins, shadow) like the real file.
  return (
    <div className="artifact-doc">
      <div className={`artifact-doc__page artifact-prose ${fmt === 'docx' ? 'artifact-prose--docx' : 'artifact-prose--md'}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{model.source || ''}</ReactMarkdown>
      </div>
    </div>
  );
};

export default ArtifactViewer;
